import { Text } from "@amzn/storm-ui";
import { range } from "lodash";
import styled from "styled-components";
import { useStore } from "zustand";

import { StyleConstants } from "../../../../config/constants";
import { useAiRecommendedInsightsLoader } from "../../../../hooks/aiRecommendedInsights/useAiRecommendedInsightsLoader";
import { useInsightFamiliesLoader } from "../../../../hooks/standaloneInsights/useInsightFamiliesLoader";
import {
    FeatureFlags,
    FeatureFlagsStore,
} from "../../../../store/featureFlags";
import { isError, isNotExecutedOrLoading } from "../../../../utils/api.utils";
import SkeletonLoader from "../../../common/SkeletonLoader";
import { AiResultsViewer } from "../aiResults/AiResultsViewer";
import { UnifiedContentCard } from "../unifiedContent/UnifiedContentCard";
import {
    StyledBackground,
    StyledInsightContentGridRenderer,
} from "../withInsightsRenderer";

const StyledInsightFamiliesViewer = styled(StyledBackground)`
    padding-top: ${({ theme }) => theme.spacing.small.default};
    margin-bottom: ${({ theme }) => theme.spacing.small.default};
`;

export const InsightFamiliesViewer: React.FunctionComponent =
    (): JSX.Element => {
        const [asyncState, families, totalHits] = useInsightFamiliesLoader();

        const isAiSearchEnabled = useStore(
            FeatureFlagsStore,
            (state) => state[FeatureFlags.AI_SEARCH],
        );

        const [aiLoadingState, aiCards, _aiRecommendations] =
            useAiRecommendedInsightsLoader();
        const showAiResults =
            isAiSearchEnabled &&
            (aiLoadingState !== "not-executed" || aiCards.length > 0);

        return (
            <StyledInsightFamiliesViewer data-testid="insights-renderer">
                {showAiResults && <AiResultsViewer />}
                {isNotExecutedOrLoading(asyncState) ? (
                    <StyledInsightContentGridRenderer
                        maxResults={4}
                        noMarginTop
                        hidden={false}
                    >
                        {range(0, 4).map((row, index) => (
                            <SkeletonLoader
                                height={`${StyleConstants.InsightCardSize}px`}
                                width="100%"
                                key={`${row}_${index}`}
                            />
                        ))}
                    </StyledInsightContentGridRenderer>
                ) : isError(asyncState) ? (
                    <Text color="secondary">
                        Failed to load standalone insights data
                    </Text>
                ) : families?.length > 0 ? (
                    <StyledInsightContentGridRenderer
                        maxResults={4}
                        noMarginTop
                        hidden={false}
                    >
                        {families.map((family, index) => (
                            <UnifiedContentCard
                                key={index}
                                content={family}
                                index={index}
                            />
                        ))}
                    </StyledInsightContentGridRenderer>
                ) : (
                    <Text color="secondary">
                        No standalone insights available
                    </Text>
                )}
            </StyledInsightFamiliesViewer>
        );
    };
